import React, { useRef, useEffect, useState } from 'react';
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import PropTypes from 'prop-types';
import ChartTooltip from './ChartTooltip';
import BarsChartTooltipContent from './BarsChartTooltipContent';
import {colors} from '../../utils/colors.js';
import {getCSSVariable} from "../../utils/getCSSVariable.js";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const BORDER_RADIUS = 6;
const VALUE_LABEL_FONT = '600 11px sans-serif';
const VALUE_LABEL_HEIGHT = 11;
const VALUE_LABEL_PADDING = 8;

// Writes each segment's value inside it, skipping any segment too small to hold the text.
const valueLabelsPlugin = (formatterRef) => ({
  id: 'valueLabels',
  afterDatasetsDraw: (chart) => {
    const {ctx} = chart;
    const isHorizontal = chart.options?.indexAxis === 'y';

    ctx.save();
    ctx.font = VALUE_LABEL_FONT;
    ctx.fillStyle = getCSSVariable('--white');
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!chart.isDatasetVisible(datasetIndex)) {
        return;
      }

      chart.getDatasetMeta(datasetIndex).data.forEach((bar, dataIndex) => {
        const value = dataset.data[dataIndex];
        if (typeof value !== 'number' || value <= 0) {
          return;
        }

        const text = formatterRef.current ? formatterRef.current(value) : String(value);
        const textWidth = ctx.measureText(text).width;
        const length = isHorizontal ? Math.abs(bar.x - bar.base) : Math.abs(bar.y - bar.base);
        const thickness = isHorizontal ? bar.height : bar.width;
        const textLength = isHorizontal ? textWidth : VALUE_LABEL_HEIGHT;
        const textThickness = isHorizontal ? VALUE_LABEL_HEIGHT : textWidth;
        if (length < textLength + VALUE_LABEL_PADDING || thickness < textThickness) {
          return;
        }

        const x = isHorizontal ? (bar.x + bar.base) / 2 : bar.x;
        const y = isHorizontal ? bar.y : (bar.y + bar.base) / 2;
        ctx.fillText(text, x, y);
      });
    });

    ctx.restore();
  },
});

const isPlainObject = (val) =>
  val !== null && typeof val === 'object' && val.constructor === Object;

const deepMerge = (target = {}, source = {}) => {
  const out = { ...target };
  Object.keys(source).forEach((key) => {
    const srcVal = source[key];
    const tgtVal = target[key];

    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      out[key] = deepMerge(tgtVal, srcVal);
    } else {
      out[key] = srcVal;
    }
  });
  return out;
};

const BarsChart = ({
  labels,
  datasets,
  width = '100%',
  height = 300,
  options = {},
  stacked = false,
  maintainAspectRatio = false,
  roundedBars = true,
  barSpacing = 2,
  cleanGrid = true,
  valueFormatter = null,
  tooltipWidth = null,
  showValues = false,
  onBarClick = null,
}) => {
  // Read through refs so a new formatter or handler on each render doesn't rebuild the chart.
  const valueFormatterRef = useRef(valueFormatter);
  valueFormatterRef.current = valueFormatter;
  const onBarClickRef = useRef(onBarClick);
  onBarClickRef.current = onBarClick;
  const isClickable = Boolean(onBarClick);

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    value: '',
    color: '',
    allDataPoints: [],
    monthLabel: '',
  });

  // The tooltip sits at fixed viewport coordinates, so leaving the chart or scrolling must hide it —
  // Chart.js only reports the pointer leaving, not the page moving under it.
  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas || !tooltip.visible) {
      return undefined;
    }

    const hideTooltip = () => {
      setTooltip((prev) => ({...prev, visible: false}));
    };
    canvas.addEventListener('mouseleave', hideTooltip);
    window.addEventListener('scroll', hideTooltip, true);

    return () => {
      canvas.removeEventListener('mouseleave', hideTooltip);
      window.removeEventListener('scroll', hideTooltip, true);
    };
  }, [tooltip.visible]);

  // Track mouse position over the canvas
  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas) return;
    const handleMouseMove = (e) => {
      if (!tooltip.visible) return;
      setTooltip((prev) => ({
        ...prev,
        x: e.clientX,
        y: e.clientY,
      }));
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [tooltip.visible]);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    const ctx = chartRef.current.getContext('2d');

    const spacingPlugin = {
      id: 'spacingPlugin',
      afterDatasetsDraw: (chart) => {
        if (!stacked || barSpacing <= 0) {
          return;
        }

        chart.data.datasets.forEach((dataset, datasetIndex) => {
          if (datasetIndex === 0) {
            return;
          }

          const meta = chart.getDatasetMeta(datasetIndex);
          const prevMeta = chart.getDatasetMeta(datasetIndex - 1);

          meta.data.forEach((bar, index) => {
            const prevBar = prevMeta.data[index];
            if (prevBar && bar) {
              const gap = barSpacing;
              if (bar.y < prevBar.y) {
                bar.y += gap;
                bar.height -= gap;
              } else {
                bar.y -= gap;
                bar.height -= gap;
              }
            }
          });
        });
      },
    };

    const defaultOptions = {
      maintainAspectRatio,
      responsive: true,
      plugins: {
        spacingPlugin,
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: (context) => {
            const tooltipModel = context.tooltip;
            const chart = context.chart;
            const canvas = chart.canvas;
            const rect = canvas.getBoundingClientRect();
            
            if (tooltipModel.opacity === 0) {
              setTooltip((prev) => ({ ...prev, visible: false }));
              return;
            }
            if (tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0) {
              const firstDp = tooltipModel.dataPoints[0];
              const dataIndex = firstDp.dataIndex;
              
             const tooltipX = rect.left + tooltipModel.caretX;
              const tooltipY = rect.top + tooltipModel.caretY;

             if (stacked) {
                const allDataPoints = [];
                let totalValue = 0;

                chart.data.datasets.forEach((dataset, datasetIndex) => {
                  const value = dataset.data[dataIndex];
                  if (value && value !== 0) {
                    let formattedValue = value;
                    if (valueFormatter && typeof value === 'number') {
                      formattedValue = valueFormatter(value);
                    }
                    allDataPoints.push({
                      label: dataset.label || '',
                      value: formattedValue,
                      rawValue: value,
                      color: Array.isArray(dataset.backgroundColor)
                        ? dataset.backgroundColor[dataIndex]
                        : dataset.backgroundColor,
                    });
                    if (typeof value === 'number') {
                      totalValue += value;
                    }
                  }
                });

                allDataPoints.sort((a, b) => (b.rawValue || 0) - (a.rawValue || 0));

               if (allDataPoints.length > 1 && valueFormatter) {
                  allDataPoints.push({
                    label: 'Total',
                    value: valueFormatter(totalValue),
                    rawValue: totalValue,
                    color: 'transparent',
                    isTotal: true,
                  });
                }

                setTooltip((prev) => ({
                  ...prev,
                  visible: true,
                  x: tooltipX,
                  y: tooltipY,
                  monthLabel: chart.data.labels[dataIndex] || '',
                  allDataPoints,
                  label: firstDp.dataset.label || firstDp.label,
                  value: '',
                  color: firstDp.element.options.backgroundColor,
                }));
              } else {
                // Non-stacked or single dataset - show single value
                const dp = firstDp;
                let formattedValue = dp.raw;
                if (valueFormatter && typeof dp.raw === 'number') {
                  formattedValue = valueFormatter(dp.raw);
                }
                setTooltip((prev) => ({
                  ...prev,
                  visible: true,
                  x: tooltipX,
                  y: tooltipY,
                  label: dp.dataset.label || dp.label,
                  value: formattedValue,
                  color: dp.element.options.backgroundColor,
                  allDataPoints: [],
                  monthLabel: '',
                }));
              }
            }
          },
        },
      },
      scales: {
        x: {
          stacked: stacked,
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: colors.bgDark,
            font: { size: 12 },
          },
        },
        y: {
          stacked: stacked,
          grid: cleanGrid
            ? {
              display: true,
              drawBorder: false,
              color: getCSSVariable('--bg-dark'),
              lineWidth: 1,
            }
            : { display: true },
          border: { display: false },
          ticks: {
            color: colors.bgDark,
            font: { size: 12 },
          },
        },
      },
      hover: {
        mode: 'nearest',
        intersect: true,
      },
      elements: {
        bar: {
          inflateAmount: (ctx) => (ctx.raw ? 'auto' : 0),
          borderSkipped: false,
          borderRadius: (ctx) => {
            const { datasetIndex, dataIndex, chart } = ctx;
            const value = ctx.raw;

            if (!stacked) {
              return roundedBars ? BORDER_RADIUS : 0;
            }

            if (value === 0) {
              return 0;
            }

            const drawnIndexes = chart.data.datasets
              .map((dataset, index) => index)
              .filter((index) => chart.isDatasetVisible(index) &&
                (chart.data.datasets[index].data[dataIndex] ?? 0) !== 0);
            const isStart = drawnIndexes[0] === datasetIndex;
            const isEnd = drawnIndexes[drawnIndexes.length - 1] === datasetIndex;

            if (isStart && isEnd) {
              return BORDER_RADIUS;
            }

            if (!isStart && !isEnd) {
              return 0;
            }

            const isHorizontal = chart.options?.indexAxis === 'y';

            if (isHorizontal) {
              return {
                topLeft: isStart ? BORDER_RADIUS : 0,
                bottomLeft: isStart ? BORDER_RADIUS : 0,
                topRight: isEnd ? BORDER_RADIUS : 0,
                bottomRight: isEnd ? BORDER_RADIUS : 0,
              };
            }

            return {
              bottomLeft: isStart ? BORDER_RADIUS : 0,
              bottomRight: isStart ? BORDER_RADIUS : 0,
              topLeft: isEnd ? BORDER_RADIUS : 0,
              topRight: isEnd ? BORDER_RADIUS : 0,
            };
          },
        },
      },
      datasets: {
        bar: {
          barThickness: 20,
          maxBarThickness: 30,
        },
      },
      layout: {
        padding: {
          top: 10,
          bottom: 10,
        },
      },
    };

    if (isClickable) {
      defaultOptions.onClick = (event, elements) => {
        if (elements.length > 0 && onBarClickRef.current) {
          onBarClickRef.current(elements[0].index);
        }
      };
      defaultOptions.onHover = (event, elements) => {
        event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      };
    }

    const mergedOptions = deepMerge(defaultOptions, options || {});

    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets,
      },
      options: mergedOptions,
      plugins: showValues ? [valueLabelsPlugin(valueFormatterRef)] : [],
    });

    return () => {
      chartInstance.current && chartInstance.current.destroy();
    };
  }, [labels, datasets, options, stacked, maintainAspectRatio, roundedBars, barSpacing, cleanGrid, showValues, isClickable]);

  return (
    <div style={{ position: 'relative', width, height }}>
      <canvas ref={chartRef} />
      <ChartTooltip
        visible={tooltip.visible}
        x={tooltip.x}
        y={tooltip.y}
        content={
          <BarsChartTooltipContent
            allDataPoints={tooltip.allDataPoints}
            monthLabel={tooltip.monthLabel}
            label={tooltip.label}
            value={tooltip.value}
            color={tooltip.color}
            tooltipWidth={tooltipWidth}
          />
        }
      />
    </div>
  );
};

BarsChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.number).isRequired,
      backgroundColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      borderColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      borderWidth: PropTypes.number,
      yAxisID: PropTypes.string,
    })
  ).isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.object,
  stacked: PropTypes.bool,
  maintainAspectRatio: PropTypes.bool,
  roundedBars: PropTypes.bool,
  barSpacing: PropTypes.number,
  cleanGrid: PropTypes.bool,
  valueFormatter: PropTypes.func,
  tooltipWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showValues: PropTypes.bool,
  onBarClick: PropTypes.func,
};

BarsChart.defaultProps = {
  width: '100%',
  height: 300,
  options: {},
  stacked: false,
  maintainAspectRatio: false,
  roundedBars: true,
  barSpacing: 2,
  cleanGrid: true,
  valueFormatter: null,
  tooltipWidth: null,
  showValues: false,
  onBarClick: null,
};

export default BarsChart;
